import { DeleteOutlined, HolderOutlined, PlusOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import styled from '@emotion/styled';
import { Button, Select, Tooltip } from 'antd';
import { arrayMoveImmutable } from 'array-move';
import { ReactNode, useState } from 'react';
import {
  SortableHandle,
  SortableElement,
  SortableContainer,
  SortEndHandler,
} from 'react-sortable-hoc';
import { ActivityButton, QuickReplyButton } from './ButtonEditor';
import useSmartField from 'hooks/useSmartField';
import { FV } from '../TemplateForm/share';
import FormField from 'components/FormField';
import { useTranslate } from '@tolgee/react';

const SelectWrapper = styled.div`
  width: 160px;
  margin-bottom: 8px;
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 0 6px;
  margin-bottom: 10px;
  background: var(--fett-erp-card-bg);
  > div {
    flex: 1 0 0;
  }
  > button {
    width: 16px;
    padding: 0;
    margin: 0 2px;
    &:first-of-type {
      cursor: row-resize;
    }
  }
`;

const draggingClassName = css`
  background: #fafafa;
  opacity: 0.7;
  visibility: visible;
  z-index: 100000;
  pointer-events: auto !important;
`;

const defaultQuickReply = {
  type: 'QUICK_REPLY',
  text: '',
} as const;

const defaultActivityPhone = {
  type: 'ACTIVITY',
  optionType: 'PHONE_NUM',
  text: '',
  region: undefined,
  phone: '',
} as const;

const defaultActivityUrl = {
  type: 'ACTIVITY',
  optionType: 'URL',
  text: '',
  urlType: undefined,
  url: '',
} as const;

const SortDragger = SortableHandle(() => <Button type="text" icon={<HolderOutlined />} />);

interface SortElementProps {
  content: JSX.Element;
  onDelete: () => void;
}

const SortElement = SortableElement<SortElementProps>((props: SortElementProps) => {
  const { content, onDelete } = props;

  return (
    <ButtonContainer>
      <SortDragger />
      {content}
      <Button type="text" onClick={onDelete}>
        <DeleteOutlined />
      </Button>
    </ButtonContainer>
  );
});

interface SortListProps {
  list: ReactNode[];
}

const SortList = SortableContainer<SortListProps>(({ list }: SortListProps) => <div>{list}</div>);

function ButtonsField({ region }: { region: Aide.WhatsAppRegion }) {
  const { t } = useTranslate();

  const {
    field: { value: buttons = [], onChange: onButtonsChange },
  } = useSmartField<Partial<Required<Required<FV['regionTemplateMap']>['CN']>['buttons'][0]>[]>(
    `regionTemplateMap.${region}.buttons`,
  );

  const [type, setType] = useState<Aide.WhatsAppButton['type'] | undefined>(() => {
    if (buttons.length === 0) {
      return undefined;
    }

    return buttons[0].type;
  });

  const activityButtonLimit = 2;
  const quickReplyButtonLimit = 3;

  const typeSelect = (
    <Select
      allowClear
      placeholder={t('无')}
      value={type}
      onChange={(type) => {
        setType(type);

        if (type === 'ACTIVITY') {
          onButtonsChange([defaultActivityPhone]);
        } else if (type === 'QUICK_REPLY') {
          onButtonsChange([defaultQuickReply]);
        } else {
          onButtonsChange([]);
        }
      }}
      options={[
        {
          value: 'QUICK_REPLY',
          label: t('快速回复'),
        },
        {
          value: 'ACTIVITY',
          label: t('行动号召'),
        },
      ]}
    />
  );

  const onSortEnd: SortEndHandler = ({ oldIndex, newIndex }) => {
    onButtonsChange(arrayMoveImmutable(buttons, oldIndex, newIndex));
  };

  const onDelete = (index: number) => {
    onButtonsChange([...buttons.slice(0, index), ...buttons.slice(index + 1)]);
  };

  const sortList = (
    <SortList
      useDragHandle
      onSortEnd={onSortEnd}
      helperClass={draggingClassName}
      list={buttons.map((_, index) => {
        if (type === 'QUICK_REPLY') {
          return (
            <SortElement
              index={index}
              content={<QuickReplyButton region={region} index={index} />}
              onDelete={() => onDelete(index)}
            />
          );
        }

        if (type === 'ACTIVITY') {
          return (
            <SortElement
              index={index}
              content={
                <ActivityButton
                  region={region}
                  index={index}
                  optionTypeDisabled={buttons.length >= activityButtonLimit}
                />
              }
              onDelete={() => onDelete(index)}
            />
          );
        }

        return null;
      })}
    />
  );

  const addButton = (() => {
    if (!type) {
      return null;
    }

    if (type === 'ACTIVITY' && buttons.length >= activityButtonLimit) {
      return (
        <Tooltip title={t('最多设置x个按钮', { count: activityButtonLimit })}>
          <Button disabled icon={<PlusOutlined />}>
            {t('添加按钮')}
          </Button>
        </Tooltip>
      );
    }

    if (type === 'QUICK_REPLY' && buttons.length >= quickReplyButtonLimit) {
      return (
        <Tooltip title={t('最多设置x个按钮', { count: quickReplyButtonLimit })}>
          <Button disabled icon={<PlusOutlined />}>
            {t('添加按钮')}
          </Button>
        </Tooltip>
      );
    }

    return (
      <Button
        icon={<PlusOutlined />}
        onClick={() => {
          if (type === 'ACTIVITY') {
            const optionType: Aide.WhatsAppActivityButton['optionType'] =
              (['PHONE_NUM', 'URL'] as const).find((optionType) =>
                buttons.every(
                  (button) => (button as Aide.WhatsAppActivityButton).optionType !== optionType,
                ),
              ) || 'PHONE_NUM';

            if (optionType === 'PHONE_NUM') {
              onButtonsChange([...buttons, defaultActivityPhone]);
            }

            if (optionType === 'URL') {
              onButtonsChange([...buttons, defaultActivityUrl]);
            }
          }

          if (type === 'QUICK_REPLY') {
            onButtonsChange([...buttons, defaultQuickReply]);
          }
        }}
      >
        {t('添加按钮')}
      </Button>
    );
  })();

  return (
    <FormField<Required<Required<FV['regionTemplateMap']>['CN']>['buttons']>
      name={`regionTemplateMap.${region}.buttons`}
      label={t('按钮')}
    >
      <SelectWrapper>{typeSelect}</SelectWrapper>
      {sortList}
      {addButton}
    </FormField>
  );
}

export default ButtonsField;
